import { useQuery, type QueryKey } from '@tanstack/react-query';
import service from '@Service';
import { REPORT_LIST_STALE_MS } from './useShiftReports';
import type {
  EventReportSummary,
  EventReportDetail,
  EventStaffMember,
} from '@Types/eventReport';

export const eventReportKeys = {
  forSoul: (soulId: string) => ['event-reports', 'soul', soulId] as const,
  detail: (id: string) => ['event-report', id] as const,
  staff: (id: string) => ['event-report', id, 'staff'] as const,
};

export function useEventReports({
  soulId,
  enabled = true,
  refetchInterval = false,
}: {
  soulId?: string;
  enabled?: boolean;
  refetchInterval?: number | false;
}) {
  return useQuery<EventReportSummary[]>({
    queryKey: eventReportKeys.forSoul(soulId!) as unknown as QueryKey,
    queryFn: () => service.getEventReports({ soulId: soulId! }),
    enabled: enabled && !!soulId,
    refetchInterval,
    staleTime: REPORT_LIST_STALE_MS,
  });
}

export function useEventReport({
  id,
  enabled = true,
}: {
  id?: string;
  enabled?: boolean;
}) {
  return useQuery<EventReportDetail>({
    queryKey: eventReportKeys.detail(id!) as unknown as QueryKey,
    queryFn: () => service.getEventReport(id!),
    enabled: enabled && !!id,
    // Unlike the other rollups, a forced re-run upserts the same row in place, so
    // the detail is only held as long as the staff lookup below.
    staleTime: 5 * 60 * 1000,
  });
}

export function useEventStaff({
  id,
  enabled = true,
}: {
  id?: string;
  enabled?: boolean;
}) {
  return useQuery<EventStaffMember[]>({
    queryKey: eventReportKeys.staff(id!) as unknown as QueryKey,
    queryFn: () => service.getEventStaff(id!),
    enabled: enabled && !!id,
    // Live warehouse read — don't hammer it; refetch sparingly.
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
}
